/* eslint-disable */
/**
 * Frontend Profit Calculation
 *
 * Ported from functions/propertyIngestionV2/underwriting/profitCalculation.js
 * Pure math — no Firestore, no side effects.
 *
 * Formulas:
 * - Holding Cost = (PurchasePrice + RepairCost) × AnnualInterestRate / 12 × HoldMonths
 * - Commission Cost = ARV × CommissionRate
 * - Title Cost = ARV × TitleRate
 * - Monthly Expenses = (PropertyTaxes + Insurance + HOAFees) × HoldMonths
 * - Financing Points (loan-based) = ((PurchasePrice × (1 − DownPayment)) + RepairCost) × PointsRate
 * - Profit = ARV - RepairCost - PurchasePrice - HoldingCost - CommissionCost - TitleCost - MonthlyExpenses - FinancingPoints
 * - Profit % = (Profit / ARV) × 100
 *
 * ROI (calculateROI):
 * - Cash Invested = Down Payment + Title Cost + Total Holding Costs (loan payments + monthly expenses)
 * - ROI % = (Profit / Cash Invested) × 100
 */

export const DEFAULT_UNDERWRITING = {
    annualInterestRate: 0.12,
    holdMonths: 4,
    titleRate: 0.015,
    commissionRate: 0.04,
    pointsRate: 0.02,
    downPayment: 0,
    targetProfitPercentage: 0.10,
};

const safeNum = (v) => (typeof v === 'number' && !isNaN(v) && v >= 0) ? v : 0;

export const calculateProfit = ({
    arv,
    repairCost,
    purchasePrice,
    propertyTaxes = 0,
    insurance = 0,
    hoaFees = 0,
    underwritingValues = DEFAULT_UNDERWRITING
}) => {
    const hasARV = arv !== null && arv !== undefined && !isNaN(arv) && arv > 0;
    const hasRepairCost = repairCost !== null && repairCost !== undefined && !isNaN(repairCost) && repairCost >= 0;
    const hasPurchasePrice = purchasePrice !== null && purchasePrice !== undefined && !isNaN(purchasePrice) && purchasePrice > 0;

    if (!hasARV || !hasRepairCost || !hasPurchasePrice) {
        return {
            hasAllData: false,
            estimatedProfit: null,
            estimatedProfitPercentage: null,
            purchasePricePercentageOfARV: null,
            holdingCost: null,
            titleCost: null,
            commissionCost: null,
            totalMonthlyExpenses: null,
            financingPoints: null,
        };
    }

    const annualRate = underwritingValues.annualInterestRate ?? DEFAULT_UNDERWRITING.annualInterestRate;
    const holdMonths = underwritingValues.holdMonths ?? DEFAULT_UNDERWRITING.holdMonths;
    const titleRate = underwritingValues.titleRate ?? DEFAULT_UNDERWRITING.titleRate;
    const commissionRate = underwritingValues.commissionRate ?? DEFAULT_UNDERWRITING.commissionRate;
    const pointsRate = underwritingValues.pointsRate ?? DEFAULT_UNDERWRITING.pointsRate;
    const downPayment = underwritingValues.downPayment ?? DEFAULT_UNDERWRITING.downPayment;
    const safeDownPayment = (typeof downPayment === 'number' && !isNaN(downPayment) && downPayment >= 0 && downPayment <= 1) ? downPayment : 0;
    const loanAmount = purchasePrice * (1 - safeDownPayment) + repairCost;

    const holdingCost = Math.round(((purchasePrice + repairCost) * annualRate / 12 * holdMonths) * 100) / 100;
    const titleCost = Math.round((arv * titleRate) * 100) / 100;
    const commissionCost = Math.round((arv * commissionRate) * 100) / 100;
    const totalMonthlyExpenses = Math.round((safeNum(propertyTaxes) + safeNum(insurance) + safeNum(hoaFees)) * holdMonths * 100) / 100;
    const financingPoints = Math.round((loanAmount * pointsRate) * 100) / 100;

    let estimatedProfit = arv - repairCost - purchasePrice - holdingCost - commissionCost - titleCost - totalMonthlyExpenses - financingPoints;
    estimatedProfit = Math.round(estimatedProfit * 100) / 100;

    let estimatedProfitPercentage = null;
    let purchasePricePercentageOfARV = null;
    if (arv > 0) {
        estimatedProfitPercentage = Math.round((estimatedProfit / arv) * 10000) / 100;
        purchasePricePercentageOfARV = Math.round((purchasePrice / arv) * 10000) / 100;
    }

    return {
        hasAllData: true,
        estimatedProfit,
        estimatedProfitPercentage,
        purchasePricePercentageOfARV,
        holdingCost,
        titleCost,
        commissionCost,
        totalMonthlyExpenses,
        financingPoints,
        underwritingUsed: {
            annualInterestRate: annualRate,
            holdMonths,
            titleRate,
            commissionRate,
            pointsRate
        }
    };
};

/**
 * Solve for MAO (Max Allowable Offer) — the purchase price at which estimated profit
 * equals the target profit percentage of ARV. Ports the algebraic fixed-point solve
 * from LeadDetail.js's mlsWalkthrough/propertyLeadWalkthrough memos. Used so multiple
 * callers can produce identical MAO numbers without re-deriving the formula.
 *
 * Why algebraic vs single-pass:
 *   Holding cost and financing points scale with the purchase price, but the purchase
 *   price *is* the MAO we're solving for — a recursive constraint. Plugging asking
 *   price into calculateProfit's purchasePrice systematically over-states costs and
 *   under-states MAO whenever asking ≠ the eventual offer.
 *
 * @param {number} arv - After-Repair Value
 * @param {number} repairCost - Estimated repair cost
 * @param {number} propertyTaxes - Monthly property taxes
 * @param {number} insurance - Monthly insurance
 * @param {number} hoaFees - Monthly HOA fees
 * @param {number} targetProfitPercent - Target profit as a percentage (10, not 0.10)
 * @param {number} wholesaleFee - Wholesale fee. Treatment depends on dealMode (see below).
 * @param {string} dealMode - 'mls' (default) or 'dts'.
 *   - mls/propertyLead: subtracts wholesaleFee post-solve (matches mlsWalkthrough/propertyLeadWalkthrough)
 *   - dts: embeds wholesaleFee × (1 + holdingRateFactor) inside the numerator
 *     (matches dtsWalkthrough — accounts for holding cost on the wholesale spread)
 * @param {Object} underwritingValues - { annualInterestRate, holdMonths, titleRate, commissionRate, pointsRate, downPayment }
 * @returns {Object} { hasData, mao, targetProfitDollars, closingCosts, monthlyExpenses,
 *                    holdingCost, financingCostsPoints, totalAcquisitionHoldingCosts,
 *                    wholesaleFee, contractPriceToBuyer }
 */
export const solveMao = ({
    arv,
    repairCost,
    propertyTaxes = 0,
    insurance = 0,
    hoaFees = 0,
    targetProfitPercent = 0,
    wholesaleFee = 0,
    dealMode = 'mls',
    underwritingValues = DEFAULT_UNDERWRITING,
}) => {
    if (!arv || arv <= 0) return { hasData: false };

    const annualRate = underwritingValues.annualInterestRate ?? DEFAULT_UNDERWRITING.annualInterestRate;
    const holdMonths = underwritingValues.holdMonths ?? DEFAULT_UNDERWRITING.holdMonths;
    const titleRate = underwritingValues.titleRate ?? DEFAULT_UNDERWRITING.titleRate;
    const commissionRate = underwritingValues.commissionRate ?? DEFAULT_UNDERWRITING.commissionRate;
    const pointsRate = underwritingValues.pointsRate ?? DEFAULT_UNDERWRITING.pointsRate;
    const downPayment = underwritingValues.downPayment ?? DEFAULT_UNDERWRITING.downPayment;
    const safeDownPayment = (typeof downPayment === 'number' && !isNaN(downPayment) && downPayment >= 0 && downPayment <= 1) ? downPayment : 0;

    const safeRepair = (typeof repairCost === 'number' && repairCost > 0) ? repairCost : 0;
    const safeTaxes = (typeof propertyTaxes === 'number' && propertyTaxes >= 0) ? propertyTaxes : 0;
    const safeInsurance = (typeof insurance === 'number' && insurance >= 0) ? insurance : 0;
    const safeHoa = (typeof hoaFees === 'number' && hoaFees >= 0) ? hoaFees : 0;
    const safeTargetPct = (typeof targetProfitPercent === 'number' && !isNaN(targetProfitPercent)) ? targetProfitPercent : 0;
    const safeWholesale = (typeof wholesaleFee === 'number' && wholesaleFee > 0) ? wholesaleFee : 0;

    const holdingRateFactor = (annualRate / 12) * holdMonths;
    const targetProfitDollars = Math.round((arv * safeTargetPct / 100) * 100) / 100;
    const closingCosts = Math.round(arv * (commissionRate + titleRate) * 100) / 100;
    const monthlyExpenses = Math.round((safeTaxes + safeInsurance + safeHoa) * holdMonths * 100) / 100;

    const denominator = 1 + holdingRateFactor + pointsRate * (1 - safeDownPayment);
    if (denominator <= 0) return { hasData: false };

    // DTS embeds the wholesale fee × (1 + holdingRateFactor) inside the numerator;
    // mls/propertyLead subtracts the fee from the solved MAO at the end.
    // The two collapse to the same answer when wholesaleFee === 0.
    //
    // Negative-MAO handling matches V1's per-flow behavior:
    //   - mls/propertyLead clamp to ≥ 0 (LeadDetail.js:1133, :1229)
    //   - dts preserves the signed value so a negative result surfaces "the deal
    //     can't pencil at this target profit" rather than silently reading $0
    //     (LeadDetail.js:1919). The ledger renderer paints a negative MAO red.
    const isDts = dealMode === 'dts';
    const numerator =
        arv -
        safeRepair -
        closingCosts -
        monthlyExpenses -
        targetProfitDollars -
        ((holdingRateFactor + pointsRate) * safeRepair) -
        (isDts ? safeWholesale * (1 + holdingRateFactor) : 0);
    const solvedMao = numerator / denominator;
    const mao = isDts
        ? Math.round(solvedMao * 100) / 100
        : Math.round(Math.max(0, solvedMao - safeWholesale) * 100) / 100;

    const holdingCost = Math.round((mao + safeRepair) * holdingRateFactor * 100) / 100;
    const loanAmount = mao * (1 - safeDownPayment) + safeRepair;
    const financingCostsPoints = Math.round(loanAmount * pointsRate * 100) / 100;
    const totalAcquisitionHoldingCosts = Math.round(
        (holdingCost + monthlyExpenses + financingCostsPoints + closingCosts) * 100
    ) / 100;

    return {
        hasData: true,
        mao,
        targetProfitDollars,
        closingCosts,
        monthlyExpenses,
        holdingCost,
        financingCostsPoints,
        totalAcquisitionHoldingCosts,
        wholesaleFee: safeWholesale,
        contractPriceToBuyer: Math.round((mao + safeWholesale) * 100) / 100,
    };
};

/**
 * Calculate ROI metrics (requires profit + propertyTaxes, insurance, hoaFees).
 * Ported from functions/propertyIngestionV2/underwriting/profitCalculation.js
 *
 * @param {Object} params - ROI calculation parameters
 * @param {number} params.estimatedProfit - Calculated profit
 * @param {number} params.purchasePrice - Purchase price
 * @param {number} params.repairCost - Repair cost
 * @param {number} params.titleCost - Title/closing cost
 * @param {number} params.holdMonths - Hold period in months
 * @param {number} params.propertyTaxes - Monthly property taxes
 * @param {number} params.insurance - Monthly insurance
 * @param {number} params.hoaFees - Monthly HOA fees
 * @param {Object} params.underwritingValues - Underwriting assumptions (annualInterestRate, pointsRate, downPayment)
 * @returns {Object|null} - ROI calculation results or null if missing data
 */
export const calculateROI = ({
    estimatedProfit,
    purchasePrice,
    repairCost,
    titleCost,
    holdMonths,
    propertyTaxes,
    insurance,
    hoaFees,
    underwritingValues = DEFAULT_UNDERWRITING
}) => {
    const isValidNumber = (value) =>
        value !== null &&
        value !== undefined &&
        typeof value === 'number' &&
        !isNaN(value) &&
        isFinite(value);

    if (!isValidNumber(propertyTaxes) || propertyTaxes < 0) return null;
    if (!isValidNumber(insurance) || insurance < 0) return null;
    if (!isValidNumber(hoaFees) || hoaFees < 0) return null;
    if (!isValidNumber(estimatedProfit)) return null;

    const annualRate = underwritingValues.annualInterestRate ?? DEFAULT_UNDERWRITING.annualInterestRate;
    const pointsRate = underwritingValues.pointsRate ?? DEFAULT_UNDERWRITING.pointsRate;
    const downPayment = underwritingValues.downPayment ?? DEFAULT_UNDERWRITING.downPayment;

    if (!isValidNumber(pointsRate) || pointsRate < 0) return null;
    if (!isValidNumber(downPayment) || downPayment < 0 || downPayment > 1) return null;

    const downPaymentDollarAmount = purchasePrice * downPayment;
    const baseLoanAmount = (purchasePrice + repairCost) - downPaymentDollarAmount;

    if (baseLoanAmount < 0) return null;

    const points = baseLoanAmount * pointsRate;
    const loanAmount = baseLoanAmount + points;

    if (loanAmount <= 0) return null;

    const monthlyLoanPayment = loanAmount * annualRate / 12;
    const totalLoanPayments = monthlyLoanPayment * holdMonths;
    const totalHoldingCosts = totalLoanPayments + (propertyTaxes * holdMonths) + (insurance * holdMonths) + (hoaFees * holdMonths);
    const cashInvested = downPaymentDollarAmount + titleCost + totalHoldingCosts;

    if (cashInvested <= 0) return null;

    const roiPercent = Math.round((estimatedProfit / cashInvested) * 10000) / 100;
    let annualizedRoiPercent = null;
    if (holdMonths > 0) {
        annualizedRoiPercent = Math.round(roiPercent * (12 / holdMonths) * 100) / 100;
    }

    return {
        roiPercent,
        annualizedRoiPercent,
        cashInvested: Math.round(cashInvested * 100) / 100,
        downPaymentDollarAmount: Math.round(downPaymentDollarAmount * 100) / 100,
        baseLoanAmount: Math.round(baseLoanAmount * 100) / 100,
        points: Math.round(points * 100) / 100,
        loanAmount: Math.round(loanAmount * 100) / 100,
        monthlyLoanPayment: Math.round(monthlyLoanPayment * 100) / 100,
        totalLoanPayments: Math.round(totalLoanPayments * 100) / 100,
        totalHoldingCosts: Math.round(totalHoldingCosts * 100) / 100
    };
};
